import { Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatKip } from "@/modules/cf-feed/presentation/ui/table-helpers";
import { useCfDashboardSummary } from "../api/queries";
import { CommentMatchBreakdown } from "../ui/CommentMatchBreakdown";
import {
  DEFAULT_DASHBOARD_FILTER,
  type DashboardFilterState,
  DashboardRangeFilter,
} from "../ui/DashboardRangeFilter";
import { KpiCards } from "../ui/KpiCards";
import { OrderStatusFunnel } from "../ui/OrderStatusFunnel";
import { OrdersTrendChart } from "../ui/OrdersTrendChart";
import { RecentOrdersCard } from "../ui/RecentOrdersCard";
import { WebhookHealthCard } from "../ui/WebhookHealthCard";

export function DashboardPage() {
  const [filter, setFilter] = useState<DashboardFilterState>(
    DEFAULT_DASHBOARD_FILTER,
  );
  const { data, isLoading, isError, refetch } = useCfDashboardSummary(
    filter.params,
  );
  const summary = data?.summary;

  return (
    <>
      <Header />

      <Main>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-bold text-2xl tracking-tight">ແຜງຄວບຄຸມ</h1>
            {summary?.range ? (
              <p className="text-muted-foreground text-xs">
                ຊ່ວງຂໍ້ມູນ: {summary.range.fromDate} ຫາ {summary.range.toDate}
              </p>
            ) : null}
          </div>
          <DashboardRangeFilter value={filter} onChange={setFilter} />
        </div>

        {isError ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-10 text-muted-foreground text-sm">
              <span>ໂຫຼດຂໍ້ມູນ dashboard ບໍ່ສຳເລັດ</span>
              <button
                type="button"
                onClick={() => refetch()}
                className="text-primary text-xs underline"
              >
                ລອງໃໝ່
              </button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* แถว 1: KPI */}
            <KpiCards data={summary?.kpis} isLoading={isLoading} />

            {/* แถว 2: trend + funnel */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <OrdersTrendChart data={summary?.trend} isLoading={isLoading} />
              <OrderStatusFunnel
                data={summary?.statusCounts}
                isLoading={isLoading}
              />
            </div>

            {/* แถว 3: match breakdown + webhook health */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <CommentMatchBreakdown
                data={summary?.matchBreakdown}
                isLoading={isLoading}
              />
              <WebhookHealthCard
                data={summary?.webhook}
                isLoading={isLoading}
              />
            </div>

            {/* แถว 4: top customers + recent orders */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <Card className="lg:col-span-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">
                    ລູກຄ້າທີ່ຊື້ຫຼາຍທີ່ສຸດ
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoading && !summary ? (
                    <div className="space-y-2 py-2">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="h-8 w-full animate-pulse rounded bg-muted"
                        />
                      ))}
                    </div>
                  ) : !summary || summary.topCustomers.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground text-sm">
                      ຍັງບໍ່ມີຂໍ້ມູນ
                    </div>
                  ) : (
                    <div className="divide-y">
                      {summary.topCustomers.map((c, i) => (
                        <div
                          key={c.customerFbId}
                          className="flex items-center justify-between gap-3 py-2.5"
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="w-5 shrink-0 text-muted-foreground text-sm tabular-nums">
                              {i + 1}.
                            </span>
                            <div className="min-w-0">
                              <div className="truncate font-medium text-sm">
                                {c.customerName || c.customerFbId}
                              </div>
                              <div className="text-muted-foreground text-xs">
                                {c.orders} ອໍເດີ
                              </div>
                            </div>
                          </div>
                          <span className="shrink-0 font-medium text-sm tabular-nums">
                            {formatKip(c.revenue)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="lg:col-span-2">
                <RecentOrdersCard
                  data={summary?.recentOrders}
                  isLoading={isLoading}
                />
              </div>
            </div>
          </div>
        )}
      </Main>
    </>
  );
}
